import React, {createContext, ReactNode, useContext, useEffect, useState} from 'react';
import {useAuth} from './AuthContext';
import {AllPayrollPeriod, Branch, getAllPayrollPeriods, getBranches} from '../services/payrollService';

const SESSION_KEY = 'nuto_period_selection';
const OWNER_KEY = 'nuto_period_selection_owner';
export const ALL_BRANCHES_VALUE = 'All';

interface PeriodSelectionState {
    selectedPeriod: string | null;
    selectedPeriodValue: string | null;
    selectedBranch: string | null;
    selectedBranchName: string | null;
    /** Global "branches" filter used by the top toolbar + dashboard (supports an "All" option,
     *  unlike `selectedBranch` above which several pages treat as a required single-branch pick). */
    dashboardBranch: string;
    dashboardBranchName: string;
}

interface PeriodContextType extends PeriodSelectionState {
    setSelectedPeriod: (period: string | null) => void;
    setSelectedPeriodValue: (value: string | null) => void;
    setSelectedBranch: (branch: string | null) => void;
    setSelectedBranchName: (name: string | null) => void;
    setPeriodAndValue: (period: string | null, value: string | null) => void;
    setBranchAndName: (branch: string | null, name: string | null) => void;
    setDashboardBranch: (branch: string, name: string) => void;
    /** Payroll periods + branches, loaded once globally so both the top toolbar selectors and
     *  any page (e.g. the dashboard) can reuse them without duplicating the fetch. */
    allPeriods: AllPayrollPeriod[];
    branches: Branch[];
    optionsLoading: boolean;
    /** Re-fetches periods + branches on demand (e.g. when a page needs fresh workflowStage
     *  without requiring a full page reload). */
    refreshPeriods: () => Promise<void>;
}

const PeriodContext = createContext<PeriodContextType | null>(null);

const EMPTY_SELECTION: PeriodSelectionState = {
    selectedPeriod: null,
    selectedPeriodValue: null,
    selectedBranch: null,
    selectedBranchName: null,
    dashboardBranch: ALL_BRANCHES_VALUE,
    dashboardBranchName: '',
};

/** The saved selection belongs to the account that made it; another account must never inherit it. */
function isStoredSelectionOwnedBy(owner: string | null): boolean {
    try {
        return !!owner && sessionStorage.getItem(OWNER_KEY) === owner;
    } catch {
        return false;
    }
}

function loadFromSession(owner: string | null): PeriodSelectionState {
    try {
        const stored = isStoredSelectionOwnedBy(owner) ? sessionStorage.getItem(SESSION_KEY) : null;
        if (stored) {
            return {
                dashboardBranch: ALL_BRANCHES_VALUE,
                dashboardBranchName: '',
                ...(JSON.parse(stored) as Partial<PeriodSelectionState>),
            } as PeriodSelectionState;
        }
    } catch {
        // ignore parse errors
    }
    return EMPTY_SELECTION;
}

function saveToSession(state: PeriodSelectionState, owner: string | null): void {
    try {
        if (owner) sessionStorage.setItem(OWNER_KEY, owner);
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(state));
    } catch {
        // ignore storage errors
    }
}

interface PeriodProviderProps {
    children: ReactNode;
}

export const PeriodProvider = ({children}: PeriodProviderProps) => {
    const {token, user} = useAuth();
    const [state, setState] = useState<PeriodSelectionState>(() => loadFromSession(user));
    const [allPeriods, setAllPeriods] = useState<AllPayrollPeriod[]>([]);
    const [branches, setBranches] = useState<Branch[]>([]);
    const [optionsLoading, setOptionsLoading] = useState<boolean>(true);
    const ownerRef = React.useRef<string | null>(user);

    // Account switch while the app stays mounted: drop the previous account's selection and lists
    // immediately so no period/branch from another tenant is ever shown or used.
    useEffect(() => {
        if (ownerRef.current !== user) {
            ownerRef.current = user;
            setState(EMPTY_SELECTION);
            setAllPeriods([]);
            setBranches([]);
            setOptionsLoading(true);
        }
    }, [user]);

    useEffect(() => {
        saveToSession(state, user);
    }, [state, user]);

    // Loaded once globally (independent of the currently rendered page) so the top toolbar's
    // period/branch selectors always have data, and so pages like the dashboard don't need to
    // re-fetch the same lists themselves. Also exposed as `refreshPeriods` below so a page (e.g.
    // Reports) can re-fetch on demand without requiring a full page reload.
    const fetchPeriodsAndBranches = React.useCallback(
        (cancelledRef?: {cancelled: boolean}) => {
            if (!token) return Promise.resolve();
            setOptionsLoading(true);
            return Promise.all([getAllPayrollPeriods(token), getBranches(token)])
                .then(([periods, branchesResponse]) => {
                    if (cancelledRef?.cancelled) return;
                    setAllPeriods(periods);
                    setBranches(branchesResponse.tenants ?? []);
                    setState((prev) => {
                        const branchList = branchesResponse.tenants ?? [];
                        const selectedPeriodStillExists = !!prev.selectedPeriod && periods.some((p) => p.periodKey === prev.selectedPeriod);
                        const fallbackPeriod = periods.find((p) => p.status?.toUpperCase().includes('OPEN')) ?? periods[0];
                        // Keep the selection only while it exists for this tenant; with no periods (fresh
                        // onboarding) nothing is selected.
                        const next: PeriodSelectionState = {
                            ...prev,
                            selectedPeriod: selectedPeriodStillExists ? prev.selectedPeriod : fallbackPeriod?.periodKey ?? null,
                            selectedPeriodValue: selectedPeriodStillExists ? prev.selectedPeriodValue : fallbackPeriod?.value ?? null,
                        };
                        if (prev.selectedBranch && !branchList.some((b) => b.id === prev.selectedBranch)) {
                            next.selectedBranch = null;
                            next.selectedBranchName = null;
                        }
                        if (prev.dashboardBranch !== ALL_BRANCHES_VALUE && !branchList.some((b) => b.id === prev.dashboardBranch)) {
                            next.dashboardBranch = ALL_BRANCHES_VALUE;
                            next.dashboardBranchName = '';
                        }
                        return next;
                    });
                })
                .catch(() => {
                    if (!cancelledRef?.cancelled) {
                        setAllPeriods([]);
                        setBranches([]);
                        setState(EMPTY_SELECTION);
                    }
                })
                .finally(() => {
                    if (!cancelledRef?.cancelled) setOptionsLoading(false);
                });
        },
        [token],
    );

    useEffect(() => {
        const cancelledRef = {cancelled: false};
        fetchPeriodsAndBranches(cancelledRef);
        return () => {
            cancelledRef.cancelled = true;
        };
    }, [fetchPeriodsAndBranches]);

    const refreshPeriods = React.useCallback(() => fetchPeriodsAndBranches(), [fetchPeriodsAndBranches]);

    const setSelectedPeriod = (period: string | null) =>
        setState((prev) => ({...prev, selectedPeriod: period}));

    const setSelectedPeriodValue = (value: string | null) =>
        setState((prev) => ({...prev, selectedPeriodValue: value}));

    const setSelectedBranch = (branch: string | null) =>
        setState((prev) => ({...prev, selectedBranch: branch}));

    const setSelectedBranchName = (name: string | null) =>
        setState((prev) => ({...prev, selectedBranchName: name}));

    const setPeriodAndValue = (period: string | null, value: string | null) =>
        setState((prev) => ({...prev, selectedPeriod: period, selectedPeriodValue: value}));

    const setBranchAndName = (branch: string | null, name: string | null) =>
        setState((prev) => ({...prev, selectedBranch: branch, selectedBranchName: name}));

    const setDashboardBranch = (branch: string, name: string) =>
        setState((prev) => ({...prev, dashboardBranch: branch, dashboardBranchName: name}));

    return (
        <PeriodContext.Provider
            value={{
                ...state,
                setSelectedPeriod,
                setSelectedPeriodValue,
                setSelectedBranch,
                setSelectedBranchName,
                setPeriodAndValue,
                setBranchAndName,
                setDashboardBranch,
                allPeriods,
                branches,
                optionsLoading,
                refreshPeriods,
            }}
        >
            {children}
        </PeriodContext.Provider>
    );
};

export const usePeriod = (): PeriodContextType => {
    const context = useContext(PeriodContext);
    if (!context) {
        throw new Error('usePeriod must be used within a PeriodProvider');
    }
    return context;
};

